import { Link, useNavigate } from 'react-router-dom';
import Button from 'components/form/Button';
import { InputWithStyle } from 'components/form/Input';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { resendOTP, verifyPasswordOTP } from 'api/user';
import OtpInput from 'react-otp-input';

const ResetPasswordSchema = z.object({
  email: z.string().email({ message: 'Invalid email' }),
});

type ResetPasswordSchemaType = z.infer<typeof ResetPasswordSchema>;

const ResetPassword = () => {
  window.document.title = 'Reset Password';
  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isValid },
  } = useForm<ResetPasswordSchemaType>({
    resolver: zodResolver(ResetPasswordSchema),
    mode: 'onTouched',
  });

  const navigate = useNavigate();

  const email = watch('email');

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const submitFunction = async (email: string) => {
    setLoading(true);
    try {
      const res = await resendOTP(email, 'reset_password');

      if (res?.data) {
        setOtpSent(true);
        toast.success('otp sent to your email');
      }
    } catch (error: any) {
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        return toast.error(err.error);
      });
      console.log(error);
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    try {
      const res = await verifyPasswordOTP(email, otp);

      navigate(`/change-password?token=${res.data}`);
    } catch (error: any) {
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        return toast.error(err.error);
      });
      console.log(error);
    }
  };

  const onSubmit = (data: ResetPasswordSchemaType) => {
    submitFunction(data.email);
  };

  return (
    <section className="flex h-screen">
      <div className="flex w-full justify-center">
        <div className="hidden h-screen basis-[65%] lg:block">
          <img
            src="/assets/images/chart.jpg"
            alt="card"
            className="h-full w-full object-cover brightness-75"
          />
        </div>
        {!otpSent ? (
          <div className="my-auto basis-full px-10 md:basis-1/2 lg:basis-[35%]">
            <h3 className="mb-8 text-2xl font-medium">Reset Password</h3>
            <form className="flex flex-col gap-y-5">
              <div className="flex flex-col gap-y-1">
                <label htmlFor="email" className="font-semibold">
                  Email
                </label>
                <InputWithStyle
                  startIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                      />
                    </svg>
                  }
                  name="email"
                  placeholder="Enter email"
                  type="email"
                  control={control}
                />
              </div>

              <Button
                type="submit"
                onClick={handleSubmit(onSubmit)}
                className="rounded-md bg-blue-500 py-2 text-white"
                disabled={!isDirty || !isValid || loading}
              >
                verify
              </Button>
            </form>
            <br />
            <p className="my-2 mx-auto border"></p>
            <p>
              Don't have an account?{' '}
              <Link to="/register">
                <span className="text-blue-500">sign up</span>
              </Link>
            </p>
            <p className="mt-2">
              <Link to="/">
                <span className="text-blue-500">blockchain</span>
              </Link>
            </p>
          </div>
        ) : (
          <div className="my-auto basis-full px-10 md:basis-1/2 lg:basis-[35%]">
            <h3 className="text-brand-blue font-bold">
              Please check your email
            </h3>
            <p className="text">A 4 digit OTP code was sent to this email:</p>
            <p className="text-[16px] font-medium">{email}</p>{' '}
            <form className="flex flex-col gap-y-5">
              <div className="flex flex-col gap-y-1">
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={4}
                  renderInput={(props) => <input {...props} type="number" />}
                  inputStyle="!w-[50px] h-[50px] border border-brand-blue mx-1 text-xl rounded-md outline-none"
                  inputType="number"
                  containerStyle="flex gap-x-2 justify-between my-5 "
                />
              </div>

              <Button
                type="button"
                onClick={() => handleVerifyOTP()}
                className="rounded-md bg-blue-500 py-2 text-white"
                disabled={loading || otp.length < 4}
              >
                verify
              </Button>
            </form>
            <br />
            <p className="my-2 mx-auto border"></p>
            <p>
              Did not receive code?{' '}
              <Button
                className="mx-0 px-0 text-blue-500"
                onClick={() => submitFunction(email)}
              >
                resend otp
              </Button>
            </p>
            <p className="mt-2">
              <Link to="/">
                <span className="text-blue-500">blockchain</span>
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ResetPassword;
